import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type LoginThemeType =
  | 'anniversary-26'
  | 'corporate-blue'
  | 'elegant-dark'
  | 'modern-gradient'
  | 'standard-minimal'
  | 'standard-ocean'
  | 'standard-nature'
  | 'standard-warm'
  | 'custom';

/** Bentuk sudut kartu Sign In */
export type SignInCardShape = 'rounded' | 'rounded-lg' | 'rounded-xl' | 'rounded-2xl' | 'rounded-3xl';
/** Ketebalan border kartu Sign In */
export type SignInCardBorder = 'none' | 'thin' | 'medium' | 'thick';

export interface LoginThemeConfig {
  type: LoginThemeType;
  label: string;
  description: string;
  backgroundStyle: React.CSSProperties;
  cardStyle: 'transparent' | 'solid' | 'blur';
  showBanner: boolean;
  bannerText: string;
  showParticles: boolean;
  showWatermark: boolean;
  watermarkText: string;
  customBackgroundUrl?: string;
  /* Model bentuk & teks custom Sign In (disimpan di customConfig) */
  signInCardShape?: SignInCardShape;
  signInCardBorder?: SignInCardBorder;
  signInTitleText?: string;
  signInSubtitleText?: string;
  signInLeftTitleText?: string;
  signInLeftDescriptionText?: string;
  signInBullet1?: string;
  signInBullet2?: string;
  signInBullet3?: string;
  signInLogoUrl?: string;
  /** Gambar transparan pada panel kiri sign-in (watermark/illustration) */
  signInLeftImageUrl?: string;
  /** Opacity gambar transparan panel kiri (0-100) */
  signInLeftImageOpacity?: number;
}

export const LOGIN_THEMES: Record<LoginThemeType, LoginThemeConfig> = {
  'anniversary-26': {
    type: 'anniversary-26',
    label: '26th Anniversary',
    description: 'Tema perayaan ulang tahun ke-26 PT. Gemilang Cipta Wawasan',
    backgroundStyle: {
      background: `
        radial-gradient(ellipse at top left, rgba(255, 215, 0, 0.3) 0%, transparent 50%),
        radial-gradient(ellipse at bottom right, rgba(220, 38, 38, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse at center, rgba(59, 130, 246, 0.1) 0%, transparent 70%),
        linear-gradient(135deg, #1e3a5f 0%, #0f172a 50%, #1e1b4b 100%)
      `
    },
    cardStyle: 'blur',
    showBanner: true,
    bannerText: 'CELEBRATING 26 YEARS OF EXCELLENCE',
    showParticles: true,
    showWatermark: true,
    watermarkText: '26',
  },
  'corporate-blue': {
    type: 'corporate-blue',
    label: 'Corporate Blue',
    description: 'Tema korporat profesional dengan warna biru',
    backgroundStyle: {
      background: `linear-gradient(135deg, #1e40af 0%, #1e3a8a 50%, #1e3a5f 100%)`
    },
    cardStyle: 'solid',
    showBanner: false,
    bannerText: '',
    showParticles: false,
    showWatermark: false,
    watermarkText: '',
  },
  'elegant-dark': {
    type: 'elegant-dark',
    label: 'Elegant Dark',
    description: 'Tema elegan dengan latar gelap',
    backgroundStyle: {
      background: `
        radial-gradient(ellipse at top, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
        linear-gradient(180deg, #0f0f0f 0%, #1a1a2e 50%, #16213e 100%)
      `
    },
    cardStyle: 'blur',
    showBanner: false,
    bannerText: '',
    showParticles: true,
    showWatermark: false,
    watermarkText: '',
  },
  'modern-gradient': {
    type: 'modern-gradient',
    label: 'Modern Gradient',
    description: 'Tema modern dengan gradien warna-warni',
    backgroundStyle: {
      background: `
        linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)
      `
    },
    cardStyle: 'blur',
    showBanner: false,
    bannerText: '',
    showParticles: true,
    showWatermark: false,
    watermarkText: '',
  },
  /* 4 model tema standar */
  'standard-minimal': {
    type: 'standard-minimal',
    label: 'Standard Minimal',
    description: 'Tampilan bersih dan profesional, cocok untuk lingkungan korporat',
    backgroundStyle: {
      background: `
        linear-gradient(180deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)
      `
    },
    cardStyle: 'solid',
    showBanner: false,
    bannerText: '',
    showParticles: false,
    showWatermark: false,
    watermarkText: '',
  },
  'standard-ocean': {
    type: 'standard-ocean',
    label: 'Standard Ocean',
    description: 'Nuansa biru laut, tenang dan tepercaya',
    backgroundStyle: {
      background: `
        linear-gradient(135deg, #0ea5e9 0%, #0284c7 40%, #0369a1 100%)
      `
    },
    cardStyle: 'blur',
    showBanner: false,
    bannerText: '',
    showParticles: false,
    showWatermark: false,
    watermarkText: '',
  },
  'standard-nature': {
    type: 'standard-nature',
    label: 'Standard Nature',
    description: 'Hijau natural, segar dan menyegarkan',
    backgroundStyle: {
      background: `
        linear-gradient(135deg, #059669 0%, #047857 50%, #065f46 100%)
      `
    },
    cardStyle: 'blur',
    showBanner: false,
    bannerText: '',
    showParticles: false,
    showWatermark: false,
    watermarkText: '',
  },
  'standard-warm': {
    type: 'standard-warm',
    label: 'Standard Warm',
    description: 'Oranye dan merah hangat, penuh energi',
    backgroundStyle: {
      background: `
        linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%)
      `
    },
    cardStyle: 'blur',
    showBanner: false,
    bannerText: '',
    showParticles: false,
    showWatermark: false,
    watermarkText: '',
  },
  'custom': {
    type: 'custom',
    label: 'Custom',
    description: 'Tema kustom dengan gambar background sendiri',
    backgroundStyle: {
      background: `linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)`
    },
    cardStyle: 'blur',
    showBanner: false,
    bannerText: '',
    showParticles: false,
    showWatermark: false,
    watermarkText: '',
    customBackgroundUrl: '',
  },
};

interface LoginThemeContextType {
  currentTheme: LoginThemeConfig;
  themeType: LoginThemeType;
  setTheme: (type: LoginThemeType) => void;
  customConfig: Partial<LoginThemeConfig>;
  setCustomConfig: (config: Partial<LoginThemeConfig>) => void;
  setCustomBackgroundUrl: (url: string) => void;
  removeCustomBackground: () => void;
  saveThemeToDatabase: () => Promise<void>;
}

const LoginThemeContext = createContext<LoginThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'login-theme-config';

// Fetch theme from database or edge function (for login page)
const fetchThemeFromSource = async (): Promise<{ type: LoginThemeType; customConfig: Partial<LoginThemeConfig> } | null> => {
  try {
    // Try direct query first (authenticated users)
    const { data, error } = await supabase
      .from('app_settings')
      .select('login_theme_type, login_theme_custom_config')
      .eq('id', 'main')
      .single();

    let themeData = data;

    // If auth error, fallback to edge function (unauthenticated/login page)
    if (error && (error.code === 'PGRST301' || error.message?.includes('JWT'))) {
      const { data: publicData } = await supabase.functions.invoke('get-public-settings');
      if (publicData) {
        themeData = {
          login_theme_type: publicData.login_theme_type,
          login_theme_custom_config: publicData.login_theme_custom_config,
        };
      }
    }

    if (themeData) {
      const dbType = (themeData.login_theme_type && LOGIN_THEMES[themeData.login_theme_type as LoginThemeType])
        ? themeData.login_theme_type as LoginThemeType
        : 'modern-gradient';
      const dbCustom = (themeData.login_theme_custom_config as Partial<LoginThemeConfig>) || {};
      return { type: dbType, customConfig: dbCustom };
    }
  } catch (error) {
    console.error('Error fetching login theme:', error);
  }
  return null;
};

export const LoginThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Always start with a default, then load from DB
  const [themeType, setThemeType] = useState<LoginThemeType>('modern-gradient');
  const [customConfig, setCustomConfigState] = useState<Partial<LoginThemeConfig>>({});
  const [loaded, setLoaded] = useState(false);

  // On mount, always fetch from database (source of truth)
  useEffect(() => {
    const load = async () => {
      const result = await fetchThemeFromSource();
      if (result) {
        setThemeType(result.type);
        setCustomConfigState(result.customConfig);
        // Cache in localStorage for faster initial render next time
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ type: result.type, customConfig: result.customConfig }));
      } else {
        // Fallback to localStorage cache
        try {
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.type && LOGIN_THEMES[parsed.type as LoginThemeType]) {
              setThemeType(parsed.type);
              setCustomConfigState(parsed.customConfig || {});
            }
          }
        } catch (e) {
          console.error('Error reading localStorage:', e);
        }
      }
      setLoaded(true);
    };
    load();
  }, []);

  // Local-only setters (no auto-save to DB)
  const setTheme = useCallback((type: LoginThemeType) => {
    setThemeType(type);
  }, []);

  const setCustomConfig = useCallback((config: Partial<LoginThemeConfig>) => {
    setCustomConfigState(config);
  }, []);

  const setCustomBackgroundUrl = useCallback((url: string) => {
    setCustomConfigState(prev => ({ ...prev, customBackgroundUrl: url }));
  }, []);

  const removeCustomBackground = useCallback(() => {
    setCustomConfigState({ customBackgroundUrl: '' });
    setThemeType('modern-gradient');
  }, []);

  // Explicit save to database (called by Save button)
  const saveThemeToDatabase = useCallback(async () => {
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({
          login_theme_type: themeType,
          login_theme_custom_config: customConfig as any,
          updated_at: new Date().toISOString(),
        })
        .eq('id', 'main');

      if (error) throw error;

      // Update localStorage cache
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ type: themeType, customConfig }));
    } catch (error) {
      console.error('Error saving login theme to database:', error);
      throw error;
    }
  }, [themeType, customConfig]);

  // Build current theme (gabungkan customConfig agar bentuk/teks sign-in & logo custom ikut dipakai)
  const currentTheme: LoginThemeConfig = (() => {
    const baseTheme = LOGIN_THEMES[themeType];
    const merged = { ...baseTheme, ...customConfig };

    if (themeType === 'custom' && customConfig.customBackgroundUrl) {
      return {
        ...merged,
        type: 'custom' as LoginThemeType,
        backgroundStyle: {
          backgroundImage: `url(${customConfig.customBackgroundUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        },
      };
    }
    return { ...merged, type: themeType };
  })();

  return (
    <LoginThemeContext.Provider value={{ 
      currentTheme, 
      themeType,
      setTheme, 
      customConfig, 
      setCustomConfig, 
      setCustomBackgroundUrl,
      removeCustomBackground,
      saveThemeToDatabase,
    }}>
      {children}
    </LoginThemeContext.Provider>
  );
};

export const useLoginTheme = (): LoginThemeContextType => {
  const context = useContext(LoginThemeContext);
  if (!context) {
    throw new Error('useLoginTheme must be used within a LoginThemeProvider');
  }
  return context;
};
